import { useEffect, useRef, useState } from "react";
import { FIELD_TYPES, FieldItem } from "../../widgets/fields";
import useFieldItem from "../../widgets/fields/UseFieldItem";
import dayjs from 'dayjs';
import { cloneDeep } from "lodash";
import { Col, Row, Spinner } from "reactstrap";
import { CustomButton, showToaster, toasterTypes } from "../../widgets";
import { fetchNextVariable, getRules, updateBulkForm } from "./apiController";
import ModalPopup from "../../widgets/modal";
import { useDispatch } from "react-redux";
import { setLoading } from "../../store/slices/auth";
import { uploadPhoto } from "../profile/apiController";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import * as formula from "../common/formulafunction/Formulas";
import { Spin } from "antd";
import { validateForm } from "../surveyForm/apiController";


const TableViewData = (props) => {
  const { formData, existData, isMasterFlow = false, masterUidData } = props;
  const [values, fields, handleChange, { validateValues, reset, updateValue, updateError, removeErrors, updateAllError }] =
    useFieldItem({}, {}, { onValueChange });

  const [formTabData, setformTabData] = useState<any[]>([]);
  const [startDate, setStartDate] = useState(new Date());
  const [edit, setEdit] = useState(false);
  const [rules, setRules] = useState(props?.rules || []);
  const [load, setLoad] = useState(true);
  const [filledFromArray, setFilledFromArray] = useState<boolean>(false);
  const [disabledFields, setDisabledFields] = useState<string[]>([]);
  const tableContainerRef = useRef<any>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchRules();
    localStorage.setItem("versionid", Date.now().toString());
  }, []);

  const fetchRules = async () => {
    try {
      const response = await getRules(props.formData.id);
      const rulesData = response?.data || [];
      setRules(rulesData);
      fetchInitialData(rulesData);
    } catch (error) {
      console.error("Error fetching rules:", error);
      setLoad(false);
      dispatch(setLoading(false));
    }
  };

  const getUidField = () => {
    return formData.form_fields
      .flat()
      .find(f => f.field_type === "dropDownPreviousResponses");
  };

  const shouldAutoFillUidField = (field: any) => {
    return (
      field.field_type === "dropDownPreviousResponses" &&
      masterUidData?.id &&
      field.id === masterUidData.id
    );
  };

  /**
   * Add auto-filled field IDs to disabled fields list
   */
  const addAutoFilledFieldsToDisabledFields = (ruleString: string) => {
    const match = ruleString.match(/[a-f0-9]{24}/);
    const firstId: any = match ? match[0] : null;

    if (firstId) {
      setDisabledFields((prev) => {
        if (!prev.includes(firstId)) {
          return [...prev, firstId];
        }
        return prev;
      });
    }
  };

  /**
   * Process field value for validation and autofill
   */
  const processFieldValue = (field: any, value: any, language: string) => {
    let englishFieldValue = value;
    let localizedFieldValue = value;

    if (field.field_type === "dropDown" || field.field_type === "dropDownPreviousResponses") {
      englishFieldValue = value?.value || value?.label || value;
      localizedFieldValue = value?.label || value;
    } else if (field.field_type === "dateField") {
      englishFieldValue = value;
      localizedFieldValue = value;
    }

    return { englishFieldValue, localizedFieldValue };
  };

  /**
   * Validate rules for a specific field in a row
   */
  const validateRules = (field: any, rowIndex: number, colIndex: number) => {
    let hasAnyError = false;
    let errorObj = "";
    const fieldKey = `${field.id}_${rowIndex}_${colIndex}`;

    const matchedRules = (rules || []).filter(rule => rule.includes(field.id));
    if (!matchedRules.length) return false;

    // Helper to find field in the current row
    const findFieldInRow = (id: string) => {
      const currentRow = formTabData[rowIndex];
      if (!currentRow) return null;

      const fieldIndex = currentRow.findIndex(f => f.id === id);
      if (fieldIndex !== -1) {
        const targetField = currentRow[fieldIndex];
        const targetFieldKey = `${id}_${rowIndex}_${fieldIndex}`;
        return {
          field: targetField,
          value: values[targetFieldKey],
          fieldKey: targetFieldKey
        };
      }
      return null;
    };

    matchedRules.forEach((rawRule) => {
      const ruleString = rawRule.split("endrule")[0];
      const [ruleType, targetId, ruleExpr, ruleError] = ruleString.split(":").map(s => s.trim());
      const tokens = ruleExpr?.split(/( )/) || [];

      // ========== Validation Rule ==========
      if (ruleType === "validationrule" && targetId === field.id) {
        let formulaString = "";

        tokens.forEach(token => {
          const match = findFieldInRow(token);
          if (match) {
            const val = match.value;
            if (match.field.field_type === "number") {
              formulaString += parseFloat(`${val}`) || 0;
            } else if (["dateField", "dateTime"].includes(match.field.field_type)) {
              formulaString += `new Date('${val}').getTime()`;
            } else {
              formulaString += `'${val}'`;
            }
          } else {
            formulaString += token === "currentdate" ? `+new Date().getTime()` : token;
          }
        });

        try {
          const isValid = eval(formulaString);
          if (!isValid) {
            errorObj = ruleError?.replace(/'/g, "") || "Validation error";
            hasAnyError = true;
          }
          updateError(fieldKey, {
            hasError: !!errorObj,
            errorMsg: errorObj,
          });
        } catch (err) {
          console.error("Error in validation eval:", err);
        }
      }

      // ========== Autofill Rule ==========
      if (ruleType === "autofillrule") {
        addAutoFilledFieldsToDisabledFields(targetId);
        let formulaString = "";

        tokens.forEach(token => {
          const match = findFieldInRow(token);
          if (match) {
            const val = match.value;
            if (typeof val === "object") {
              formulaString += match.field.field_type === "dateField"
                ? `'${val}'`
                : `'${val?.label || ''}'`;
            } else {
              const localVal = processFieldValue(match.field, val, 'en');
              formulaString += `'${localVal.englishFieldValue || ''}'`;
            }
          } else {
            formulaString += token;
          }
        });

        let output;
        const functionName = formulaString.split('(')[0].trim();

        try {
          if (ruleExpr.includes(functionName) && typeof formula[functionName] === "function") {
            const args = formulaString.match(/\((.*)\)/)?.[1]?.trim();
            if (args) {
              output = formula[functionName](args);
            } else {
              errorObj = ruleError?.replace(/'/g, "") || "Autofill error";
            }
          } else {
            output = eval(formulaString);
          }

          const targetMatch = findFieldInRow(targetId);
          if (targetMatch) {
            updateValue({ [targetMatch.fieldKey]: output });

            // Update formTabData
            setformTabData(prev => {
              const updated = cloneDeep(prev);
              const targetFieldIndex = updated[rowIndex].findIndex(f => f.id === targetId);
              if (targetFieldIndex !== -1) {
                updated[rowIndex][targetFieldIndex].field_selected_value = output;
              }
              return updated;
            });
          }

          if (errorObj) hasAnyError = true;
          updateError(fieldKey, {
            hasError: !!errorObj,
            errorMsg: errorObj,
          });

        } catch (err) {
          console.error("Error in autofill eval:", err);
        }
      }
    });

    return hasAnyError;
  };

  /**
   * Check frontend and backend validations for a field
   */
  const checkValidation = async (field: any, rowIndex: number, colIndex: number) => {
    const fieldKey = `${field.id}_${rowIndex}_${colIndex}`;
    const errorObj: any[] = [];
    const validations = field?.validations || [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const numberRegex = /^(\+91[\-\s]?|91[\-\s]?|0)?[6-9]\d{9}$/;
    const value = values[fieldKey];

    for (const rule of validations) {
      if (rule.startsWith('FE')) {
        switch (rule) {
          case "FE-EmailFormat":
            if (value?.length) {
              const isValid = emailRegex.test(value.trim());
              errorObj.push({
                name: fieldKey,
                data: {
                  hasError: !isValid,
                  errorMsg: !isValid ? 'Please enter valid Email' : ''
                }
              });
            }
            break;

          case "FE-Integer":
            const isInteger = Number(value) === Math.floor(Number(value));
            errorObj.push({
              name: fieldKey,
              data: {
                hasError: !isInteger,
                errorMsg: !isInteger ? 'Please enter valid integer' : ''
              }
            });
            break;

          case "FE-MobileNumber":
            const stringVal = typeof value === 'string' ? value : String(value);
            if (!stringVal || stringVal.trim() === "") {
              errorObj.push({
                name: fieldKey,
                data: {
                  hasError: true,
                  errorMsg: 'Mobile number cannot be empty'
                }
              });
            } else if (!numberRegex.test(value)) {
              errorObj.push({
                name: fieldKey,
                data: {
                  hasError: true,
                  errorMsg: 'Please enter a valid Mobile number'
                }
              });
            }
            break;

          case "FE-Mandatory":
            if (!value || value === 'undefined') {
              errorObj.push({
                name: fieldKey,
                data: {
                  hasError: true,
                  errorMsg: 'Field cannot be empty'
                }
              });
            }
            break;

          case "FE-Range":
            const min = field.field_min_value;
            const max = field.field_max_value;
            const numValue = parseFloat(value);
            if (value?.length && (numValue < min || numValue > max)) {
              errorObj.push({
                name: fieldKey,
                data: {
                  hasError: true,
                  errorMsg: `Values should be between ${min} to ${max}`
                }
              });
            }
            break;

          default:
            updateError(fieldKey, { hasError: false, errorMsg: "" });
            break;
        }

      } else if (rule.startsWith('BE')) {
        const versionid: number = Number(localStorage.getItem("versionid"));
        const resObj = {
          field_id: field.id,
          value: value,
          version_id: versionid,
          record_id: "",
        };

        if (resObj.value?.length) {
          try {
            const res: any = await validateForm(formData.id, [resObj]);

            if (!res?.success) {
              errorObj.push({
                name: fieldKey,
                data: {
                  hasError: true,
                  errorMsg: res?.error[0] || "Value already used"
                }
              });
            } else {
              errorObj.push({
                name: fieldKey,
                data: {
                  hasError: false,
                  errorMsg: ""
                }
              });
            }
          } catch {
            updateError(fieldKey, { hasError: true, errorMsg: "Value already used" });
          }
        }
      }
    }

    // Check for UID field and action fields mandatory
    const isUidField = field?.field_use_type?.includes("UID");
    const skipActionField = !!field?.action_enabled;
    if (field.field_type !== "instruction" && (isUidField || skipActionField) && !value) {
      errorObj.push({
        name: fieldKey,
        data: {
          hasError: true,
          errorMsg: `Please fill the required field`
        }
      });
    }

    return errorObj;
  };

  /**
   * Check validation and apply autofill for a field
   */
  const checkValidationAndAutofill = async (field: any, rowIndex: number, colIndex: number) => {
    const fieldKey = `${field.id}_${rowIndex}_${colIndex}`;
    const errors = await checkValidation(field, rowIndex, colIndex);

    if (errors.length === 0) {
      removeErrors(fieldKey);
    } else {
      updateAllError(errors);
    }

    const isValid = !errors.some(item => item.data.hasError);
    if (!isValid) return;

    validateRules(field, rowIndex, colIndex);
  };

  /**
   * Validate all fields in all rows
   */
  const checkAllValidation = async () => {
    let errorList: any[] = [];

    for (let rowIndex = 0; rowIndex < formTabData.length; rowIndex++) {
      const row = formTabData[rowIndex];
      for (let colIndex = 0; colIndex < row.length; colIndex++) {
        const field = row[colIndex];
        const errors = await checkValidation(field, rowIndex, colIndex);
        errorList.push(...errors);
      }
    }

    if (errorList.length === 0) {
      removeErrors();
    } else {
      updateAllError(errorList);
    }

    return !errorList.some(item => item.data.hasError);
  };

  const applyAutofillRules = (combinedData: any[], rowIndex: number, rulesData: any[]) => {
    rulesData?.forEach((ruleString) => {
      if (ruleString.split(':')[0].trim() === "autofillrule") {
        const splitString = ruleString.split(':')[2];
        const targetId = ruleString.split(':')[1].trim();
        const targetFieldIndex = combinedData.findIndex(field => field.id === targetId);

        if (targetFieldIndex === -1) return;

        let formulaString = "";

        splitString.trim().split(/( )/).forEach((token) => {
          const fieldInAllTables = formData.form_fields
            .flat()
            .find(field => field.id === token);

          if (fieldInAllTables) {
            const value = fieldInAllTables.field_selected_value;
            if (value) {
              const finalValue = ['radioButton', 'checkBox', 'dropDown', 'dropDownPreviousResponses']
                .includes(fieldInAllTables.field_type)
                ? value.label
                : fieldInAllTables.field_type === "dateField"
                  ? new Date(value)
                  : value;
              formulaString += `'${finalValue}'`;
            } else {
              formulaString += '';
            }
          } else {
            formulaString += token;
          }
        });

        let output = "";
        const functionName = formulaString.split('(')[0].trim();

        if (splitString.trim().includes(functionName)) {
          const match = formulaString.match(/\((.*)\)/);
          const argument = match ? match[1].trim() : '';

          if (argument || functionName === 'autofillcurrentDate') {
            output = formula[functionName](argument);
            const fieldKey = `${targetId}_${rowIndex}_${targetFieldIndex}`;
            updateValue({ [fieldKey]: output });
            combinedData[targetFieldIndex].field_selected_value = output;
          }
        } else {
          try {
            output = eval(formulaString);
            const fieldKey = `${targetId}_${rowIndex}_${targetFieldIndex}`;
            updateValue({ [fieldKey]: output });
            combinedData[targetFieldIndex].field_selected_value = output;
          } catch (error) {
            console.error("Error evaluating formula:", error);
          }
        }
      }
    });
  };

  const fetchInitialData = (rulesData: any[]) => {
    dispatch(setLoading(true));

    formData.isTable.forEach((isTable, tableIndex) => {
      if (!isTable) return;

      let fieldDataArray: any[] = [];

      if (isMasterFlow) {
        const combinedData = cloneDeep(formData.form_fields[tableIndex]);

        combinedData.forEach((field, colIndex) => {
          if (shouldAutoFillUidField(field)) {
            field.isDisabled = true;
            const valueToSet = masterUidData.value || field.field_selected_value;
            if (valueToSet) {
              updateValue({ [`${field.id}_0_${colIndex}`]: valueToSet });
              field.field_selected_value = valueToSet;
            }
          }
        });

        applyAutofillRules(combinedData, 0, rulesData);
        fieldDataArray.push(combinedData);
        setformTabData(fieldDataArray);
        setLoad(false);
        dispatch(setLoading(false));
        return;
      }

      const uidFields = formData.form_fields[tableIndex].filter(
        (item) => item.field_type === "dropDownPreviousResponses"
      );

      if (uidFields.length === 0) {
        setLoad(false);
        dispatch(setLoading(false));
        return;
      }

      const firstUidField = uidFields[0];

      if (!Array.isArray(firstUidField?.field_value) || firstUidField.field_value.length === 0) {
        console.warn("No field values to map for dropdown field");
        setLoad(false);
        dispatch(setLoading(false));
        return;
      }

      firstUidField.field_value.forEach((uidValue, rowIndex) => {
        const rowData = cloneDeep(formData.form_fields[tableIndex]);

        rowData.forEach((field, colIndex) => {
          const fieldKey = `${field.id}_${rowIndex}_${colIndex}`;

          if (shouldAutoFillUidField(field)) {
            const valueToSet = masterUidData.value || uidValue;
            field.isDisabled = true;
            updateValue({ [fieldKey]: valueToSet });
            field.field_selected_value = valueToSet;
          }
          else if (field.field_type === "dropDownPreviousResponses") {
            updateValue({ [fieldKey]: uidValue });
            field.field_selected_value = uidValue;
          }

          if (colIndex !== 0 && existData?.fields?.length > 0) {
            const existingField = existData.fields.find(
              item => item.id === field.id && uidValue.label === existData.fields[0].field_selected_value
            );
            if (existingField) {
              updateValue({ [fieldKey]: existingField.field_selected_value });
              field.field_selected_value = existingField.field_selected_value;
            }
          }
        });

        const uidFieldsData = rowData.filter(item => item.field_type === "dropDownPreviousResponses");
        const otherFieldsData = rowData.filter(item => item.field_type !== "dropDownPreviousResponses");
        const combinedData = [...uidFieldsData, ...otherFieldsData];

        applyAutofillRules(combinedData, rowIndex, rulesData);
        fieldDataArray.push(combinedData);
      });

      if (fieldDataArray.length > 0) {
        setFilledFromArray(true);
      }
      setformTabData(fieldDataArray);
      setLoad(false);
    });

    dispatch(setLoading(false));
  };

  const createRow = (rowIndex: number, tableIndex: number = 0) => {
    const row = cloneDeep(formData.form_fields[tableIndex]);

    row.forEach((field, colIndex) => {
      if (shouldAutoFillUidField(field)) {
        field.isDisabled = true;
        const valueToSet = masterUidData.value || field.field_selected_value;
        if (valueToSet) {
          updateValue({ [`${field.id}_${rowIndex}_${colIndex}`]: valueToSet });
          field.field_selected_value = valueToSet;
        }
      }
    });

    return row;
  };

  const addRows = () => {
    const totalRows = Number(values.addcount);

    if (!totalRows || totalRows <= 0) {
      showToaster(toasterTypes.ERROR, "Please enter a valid number of rows");
      return;
    }

    formData.isTable.forEach((isTable, tableIndex) => {
      if (!isTable) return;

      const newRows: any[] = [];

      for (let i = 0; i < totalRows; i++) {
        newRows.push(createRow(i, tableIndex));
      }

      setformTabData(newRows);
    });

    updateValue({ addcount: "" });
  };

  function onValueChange(name: string, value: any, currentValues: any) {
    const [fieldId, rowIndex, colIndex] = name.split("_");

    const field = formData.form_fields
      .flat()
      .find(f => f.id === fieldId);

    const isIdentifierField = field?.field_use_type?.includes("Identifier");

    if (isIdentifierField) {
      const updatedValues = { ...currentValues, [name]: value };
      callNextApi(value, field, rowIndex, colIndex, updatedValues);
    }

    // Trigger validation on blur behavior for certain field types
    if (field && [
      "dateField", "radioButton", "dropDown", "time"
    ].includes(field.field_type)) {
      setTimeout(() => {
        checkValidationAndAutofill(field, Number(rowIndex), Number(colIndex));
      }, 100);
    }

    return [{ ...currentValues, [name]: value }];
  }

  const getFieldIdAndAnswerUpToCurrent = (
    currentRowIndex: number,
    currentColIndex: number,
    valuesSnapshot: any = values
  ) => {
    const fieldIds: string[] = [];
    const answers: (string | number)[] = [];

    if (!formTabData[currentRowIndex]) {
      return { fieldId: '', answer: '' };
    }

    const currentRow = formTabData[currentRowIndex];

    for (let colIdx = 0; colIdx <= currentColIndex; colIdx++) {
      const field = currentRow[colIdx];
      if (!field) continue;

      const fieldKey = `${field.id}_${currentRowIndex}_${colIdx}`;
      const fieldValue = valuesSnapshot[fieldKey];

      fieldIds.push(field.id);

      let valueToAdd: string | number = '';

      if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
        if (field.field_type === 'number') {
          valueToAdd = parseInt(fieldValue as string, 10) || fieldValue;
        } else if (
          field.field_type === 'dropDown' ||
          field.field_type === 'dropDownPreviousResponses'
        ) {
          valueToAdd = fieldValue?.label || fieldValue?.value || '';
        } else {
          valueToAdd = fieldValue as string;
        }
      }

      answers.push(valueToAdd);
    }

    return {
      fieldId: fieldIds.join(','),
      answer: answers.join(',')
    };
  };

  const isAlreadyOptionObject = (val: any) => {
    return (
      val &&
      typeof val === "object" &&
      typeof val.label === "string" &&
      typeof val.value === "string"
    );
  };

  const normalizeSelectedValue = (value, fieldType) => {
    if (value == null) return value;

    const needsPrev = fieldType === "dropDownPreviousResponses";

    const toOption = (val) => ({
      label: needsPrev ? val.value ?? val : val,
      value: needsPrev ? val.value ?? val : val
    });

    if (Array.isArray(value)) {
      if (value.length > 0 && isAlreadyOptionObject(value[0])) {
        return value;
      }
      return value.map(v => toOption(v));
    }

    if (isAlreadyOptionObject(value)) {
      return value;
    }

    return toOption(value);
  };

  function getValues(data, type) {
    const valuesData: any[] = [];
    if (type === "prev") {
      data.forEach(item => {
        valuesData.push({
          label: item.value,
          value: item.value
        });
      });
    } else {
      data.forEach(item => {
        valuesData.push({
          label: item,
          value: item
        });
      });
    }
    return valuesData;
  }

  function processFormFields(formFieldsData) {
    return formFieldsData.map(field => {
      const shouldTransform =
        field.field_type === "dropDown" ||
        field.field_type === "radioButton" ||
        field.field_type === "checkBox";

      const needsPrev = field.field_type === "dropDownPreviousResponses";
      const type = needsPrev ? "prev" : "";

      const safeGetValues = (values, type = "") => {
        if (!Array.isArray(values)) return values;

        if (values.length > 0 && isAlreadyOptionObject(values[0])) {
          return values;
        }

        return getValues(values, type);
      };

      const shouldNormalizeSelected =
        shouldTransform || needsPrev;

      return {
        ...field,

        field_value:
          shouldTransform || needsPrev
            ? safeGetValues(field.field_value || [], type)
            : field.field_value,
        field_selected_value: shouldNormalizeSelected
          ? normalizeSelectedValue(
            field.field_selected_value,
            field.field_type
          )
          : field.field_selected_value,

        localized_field_selected_value: null,

        localized:
          field.localized?.map(loc => ({
            ...loc,
            localized_field_value: shouldTransform
              ? safeGetValues(loc.localized_field_value || [])
              : loc.localized_field_value,

            localized_field_row_value: shouldTransform
              ? safeGetValues(loc.localized_field_row_value || [])
              : loc.localized_field_row_value
          })) || []
      };
    });
  }

  const callNextApi = async (
    value: any,
    field: any,
    rowIndex: string,
    colIndex: string,
    updatedValues: any
  ) => {
    try {
      dispatch(setLoading(true));

      const { fieldId, answer } = getFieldIdAndAnswerUpToCurrent(
        Number(rowIndex),
        Number(colIndex),
        updatedValues
      );

      const response = await fetchNextVariable(
        formData.id,
        fieldId,
        answer,
        true
      );

      if (!response?.success || !response?.data) return;

      const receivedFields = Array.isArray(response.data[0])
        ? response.data.flat()
        : response.data;

      if (receivedFields.length !== 1) {
        console.warn("Received multiple or no fields, skipping update");
        return;
      }

      const processedFields = processFormFields(receivedFields);
      const apiField = processedFields[0];

      setformTabData(prev => {
        const updated = cloneDeep(prev);
        const currentRow = updated[Number(rowIndex)];
        if (!currentRow) return prev;

        const indexInRow = currentRow.findIndex(
          f => f.id === apiField.id
        );

        if (indexInRow !== -1) {
          currentRow[indexInRow] = apiField;

          const fieldKey = `${apiField.id}_${rowIndex}_${indexInRow}`;
          if (apiField.field_selected_value !== undefined) {
            updateValue({
              [fieldKey]: apiField.field_selected_value
            });
          }
        }

        return updated;
      });
    } catch (err) {
      console.error("Identifier next-variable failed", err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const uploadDoc = async (id: string, data: FormData): Promise<{ name: string; id: string }> => {
    try {
      const response = await uploadPhoto(data);
      if (response.mediaUrl) {
        return { name: response.mediaUrl, id };
      }
      throw new Error("Upload failed");
    } catch (error) {
      console.error("Error uploading document:", error);
      throw error;
    }
  };

  /**
   * Save form data
   */
  const onSave = async () => {
    dispatch(setLoading(true));
    try {
      // Validate all fields before saving
      const isValid = await checkAllValidation();

      if (!isValid) {
        showToaster(toasterTypes.ERROR, "Please correct all validation errors before submitting");
        dispatch(setLoading(false));
        return;
      }

      // Check if there are any error messages
      const hasErrors = Object.values(fields).some(
        (field: any) => field.hasError
      );

      if (hasErrors) {
        showToaster(toasterTypes.ERROR, "Please correct all errors before submitting");
        dispatch(setLoading(false));
        return;
      }
      // Filter out completely empty rows
      const nonEmptyRows = formTabData.filter((row) =>
        row.some((field) => {
          const fieldKey = `${field.id}_${formTabData.indexOf(row)}_${row.indexOf(field)}`;
          const fieldValue = values[fieldKey];
          return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
        })
      );

      if (nonEmptyRows.length === 0) {
        showToaster(toasterTypes.ERROR, "Please fill at least one row");
        dispatch(setLoading(false));
        return;
      }

      const finalArray: any[] = [];
      const uploadPromises: Promise<{ name: string; id: string }>[] = [];

      // Process each row
      nonEmptyRows.forEach((row, rowIndex) => {
        const fields: any[] = [];

        // Add non-table fields (if any)
        formData.isTable.forEach((isTable, tableIndex) => {
          if (!isTable) {
            formData.form_fields[tableIndex]?.forEach((field) => {
              fields.push({
                id: field.id,
                field_selected_value: field.field_selected_value?.label || field.field_selected_value
              });
            });
          }
        });

        // Process row fields
        row.forEach((field, colIndex) => {
          const fieldKey = `${field.id}_${rowIndex}_${colIndex}`;
          const fieldValue = values[fieldKey];

          let fieldObj: any = { id: field.id, field_selected_value: null };

          if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
            if (field.field_type === "dropDown" || field.field_type === "dropDownPreviousResponses") {
              fieldObj.field_selected_value = fieldValue.label;
            } else if (field.field_type === "fileUpload") {
              if (typeof fieldValue !== "string" && fieldValue) {
                const formData = new FormData();
                formData.append("file", fieldValue[0]);
                uploadPromises.push(
                  uploadDoc(fieldKey, formData).catch(error => {
                    console.error(`Upload failed for field ${fieldKey}:`, error);
                    return { name: '', id: fieldKey };
                  })
                );
              } else {
                fieldObj.field_selected_value = fieldValue;
              }
            } else {
              fieldObj.field_selected_value = fieldValue;
            }
          }

          fields.push(fieldObj);
        });

        const versionId = Number(localStorage.getItem("versionid"));
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const endDate = new Date();

        finalArray.push({
          form_id: formData.id,
          version_id: versionId,
          fields,
          start_time: startDate,
          end_time: endDate,
          time_zone: timeZone
        });
      });

      // Handle file uploads
      const uploadResults = await Promise.allSettled(uploadPromises);

      uploadResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.name) {
          const { id, name } = result.value;
          // Parse the field key to get row and column indices
          const parts = id.split('_');
          const rowIndex = parseInt(parts[parts.length - 2]);
          const colIndex = parseInt(parts[parts.length - 1]);

          if (finalArray[rowIndex]?.fields[colIndex]) {
            finalArray[rowIndex].fields[colIndex].field_selected_value = name;
          }
        }
      });

      const response = await updateBulkForm(finalArray);
      if (response) {
        showToaster(toasterTypes.SUCCESS, response.message);
        props.onTableSubmit(false, true);
      }

      dispatch(setLoading(false));
    } catch (error) {
      console.error("Error saving form:", error);
      showToaster(toasterTypes.ERROR, "Failed to save form data");
      dispatch(setLoading(false));
    }
  };

  /**
   * Edit/reset functionality
   */
  const editIndex = () => {
    setformTabData([]);
    setEdit(false);
    fetchRules(); // Reload data
  };

  /**
   * Handle horizontal scroll
   */
  const handleScroll = (direction: 'left' | 'right') => {
    if (tableContainerRef.current) {
      const scrollAmount = 100;
      tableContainerRef.current.scrollLeft += direction === 'right' ? scrollAmount : -scrollAmount;
    }
  };

  return (
    <>
      {edit && (
        <ModalPopup
          modalType="delete"
          modalText="Are you sure you want to edit?"
          actionText="This will erase the data of next variables"
          closeModal={() => setEdit(false)}
          onAction={editIndex}
        />
      )}

      <div className="table-view-container">
        {formTabData.length > 0 ? (
          <>
            <div
              style={{
                position: 'absolute',
                left: '10%',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1,
                backgroundColor: 'var(--primary-color, #006BD9)',
                cursor: 'pointer',
                padding: '5px',
                borderRadius: '5px'
              }}
              onClick={() => handleScroll('left')}
            >
              <LeftOutlined style={{ color: '#fff' }} />
            </div>

            <div>Total Records: {formTabData.length}</div>

            <div ref={tableContainerRef} className="table-data">
              {formTabData.map((row, rowIndex) => (
                <table className="vertical-table" key={rowIndex}>
                  {rowIndex === 0 && (
                    <thead>
                      <tr>
                        {row.map((field, colIndex) => (
                          <th key={colIndex}>{field.field_name}</th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    <tr>
                      {row.map((field, colIndex) => {
                        const fieldKey = `${field.id}_${rowIndex}_${colIndex}`;

                        // Special case: first column UID field in non-master flow
                        if (
                          colIndex === 0 &&
                          field.field_type === "dropDownPreviousResponses" &&
                          !isMasterFlow
                        ) {
                          return (
                            <td key={colIndex}>
                              {field.field_value[rowIndex]?.label || ''}
                            </td>
                          );
                        }

                        const fieldError = fields && fields[fieldKey];

                        return (
                          <td key={colIndex}>
                            <FieldItem
                              className="dynamic-item"
                              name={fieldKey}
                              placeholder={
                                ["number", "textBox", "textArea"].includes(field.field_type)
                                  ? "Enter"
                                  : "Select"
                              }
                              isDisabled={field?.isDisabled || false}
                              label={field.field_name}
                              type={
                                field.field_type === "fileUpload" ? FIELD_TYPES.FILE_UPLOAD :
                                  field.field_type === "dateField" ? FIELD_TYPES.DATEPICKER_NEW :
                                    field.field_type === "dropDown" ? FIELD_TYPES.DROP_DOWN :
                                      field.field_type === "dropDownPreviousResponses" ? FIELD_TYPES.DROP_DOWN :
                                        field.field_type === "boolean" ? FIELD_TYPES.BOOLEAN :
                                          field.field_type === "radioButton" ? FIELD_TYPES.RADIO_BUTTON :
                                            field.field_type === "checkBox" ? FIELD_TYPES.CHECK_BOX :
                                              field.field_type === "textArea" ? FIELD_TYPES.TEXT_AREA :
                                                field.field_type === "textBox" ? FIELD_TYPES.TEXT :
                                                  field.field_type === "time" ? FIELD_TYPES.TIME_PICKER :
                                                    field.field_type === "dateTime" ? FIELD_TYPES.DATE_TIME_PICKER :
                                                      FIELD_TYPES.TEXT
                              }
                              isNumber={field.field_type === "number"}
                              value={
                                field.field_type === "dateField" && values[fieldKey]
                                  ? dayjs(values[fieldKey])
                                  : values[fieldKey]
                              }
                              values={field.field_value}
                              isListed={true}
                              onChange={(...e) => handleChange(fieldKey, ...e)}
                              touched={!!fieldError?.hasError}
                              error={fieldError?.errorMsg}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              ))}
            </div>

            <div
              style={{
                position: 'absolute',
                right: '5px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1,
                backgroundColor: 'var(--primary-color, #006BD9)',
                cursor: 'pointer',
                padding: '5px',
                borderRadius: '5px'
              }}
              onClick={() => handleScroll('right')}
            >
              <RightOutlined style={{ color: '#fff' }} />
            </div>
          </>
        ) : (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {load ? (
              <Spin />
            ) : isMasterFlow ? (
              "Loading..."
            ) : (
              "Data Entry in Table format is not possible due to non-available parent data. Please contact admin"
            )}
          </div>
        )}

        {!filledFromArray && (
          <div className="p-4" style={{ display: "flex", justifyContent: "center", gap: "10px", alignItems: "center" }}>
            <span>Add Records</span>
            <FieldItem
              className="dynamic-item"
              name="addcount"
              value={values.addcount}
              type={FIELD_TYPES.TEXT}
              isNumber={true}
              placeholder="Enter number of records"
              onChange={(...e) => handleChange('addcount', ...e)}
            />
            <CustomButton type="primary" onClick={addRows} text="Add" />
          </div>
        )}

        {formTabData.length > 0 && (
          <Row className="footer-card">
            <Col className="p-0" />
            <Col className="p-0 d-flex m-4 justify-content-end">
              <CustomButton type="primary" onClick={onSave} text="Submit" />
            </Col>
          </Row>
        )}
      </div>
    </>
  );
};

export default TableViewData;